/**
 * DLS color tokens for use in JS/TS contexts (Recharts, jsPDF, SVG, inline styles).
 * Sourced from official Neeve DLS (bitbucket.org/iotium/dls-neeve → tokens.json).
 *
 * For Tailwind/className contexts, use the `text-*` / `bg-*` classes from tailwind.config.js.
 * This file is for code that needs raw hex strings (chart fills, PDF drawing, canvas, etc.).
 */

// ── Core brand colors ──────────────────────────────────────────────────
export const CORE = {
  neeveGreen: '#065545',
  darkGreen: '#012a22',
  neeveTertiary: '#2f8a77',
  white: '#ffffff',
} as const;

// ── Brand accent colors (blue scale) ───────────────────────────────────
export const BRAND = {
  primary: '#225e7c',
  secondary: '#268fb4',
  tertiary: '#68c2de',
  quarternary: '#d6f0f7',
  contrast: '#eff9fc',
} as const;

// ── SARA colors (amber scale) ──────────────────────────────────────────
export const SARA = {
  primary: '#f5c16a',
  secondary: '#fae0ae',
  tertiary: '#fef9ee',
  contrast: '#773b17',
} as const;

// ── Status colors (4 shades each) ──────────────────────────────────────
export const STATUS = {
  success: {
    primary: '#11aa8b',
    secondary: '#9ad6c5',
    tertiary: '#dff5ed',
    quarternary: '#f2fffb',
  },
  warning: {
    primary: '#c63d08',
    secondary: '#ff9038',
    tertiary: '#ffd6a8',
    quarternary: '#fff7ed',
  },
  danger: {
    primary: '#b71e1e',
    secondary: '#f67373',
    tertiary: '#fdcbcb',
    quarternary: '#fef2f2',
  },
  inactive: {
    primary: '#4f4f4f',
    secondary: '#888888',
    tertiary: '#d1d1d1',
    quarternary: '#f6f6f6',
  },
} as const;

// ── UI neutral colors ──────────────────────────────────────────────────
export const NEUTRAL = {
  primary: '#171717',
  secondary: '#5d5d5d',
  tertiary: '#b0b0b0',
  quarternary: '#e7e7e7',
  contrast: '#f6f6f6',
} as const;

// ── DLS color scales (selected values for charts/viz) ──────────────────
export const SCALES = {
  green: {
    50: '#ecfdf6', 100: '#d1fae8', 200: '#a7f3d5', 300: '#6de8bf',
    400: '#33d4a3', 500: '#0fba8b', 600: '#049772', 700: '#03795e',
    800: '#05604c', 900: '#065545', 950: '#022c25',
  },
  red: {
    50: '#fef2f2', 100: '#fee2e2', 200: '#fdcbcb', 300: '#fba6a6',
    400: '#f67373', 500: '#ec4747', 600: '#cc2424', 700: '#b71e1e',
    800: '#971d1d', 900: '#7d1f1f', 950: '#440b0b',
  },
  orange: {
    50: '#fff7ed', 100: '#ffedd4', 200: '#ffd6a8', 300: '#ffb971',
    400: '#ff9038', 500: '#fe7920', 600: '#ef5407', 700: '#c63d08',
    800: '#9d320f', 900: '#7e2b10', 950: '#441206',
  },
  gray: {
    50: '#f6f6f6', 100: '#e7e7e7', 200: '#d1d1d1', 300: '#b0b0b0',
    400: '#888888', 500: '#6d6d6d', 600: '#5d5d5d', 700: '#4f4f4f',
    800: '#454545', 900: '#3d3d3d', 950: '#171717',
  },
  blue: {
    50: '#eff9fc', 100: '#d6f0f7', 200: '#b2e1ef', 300: '#68c2de',
    400: '#41abcf', 500: '#268fb4', 600: '#227398', 700: '#225e7c',
  },
  amber: {
    50: '#fef9ee', 100: '#fdf1d7', 200: '#fae0ae', 300: '#f5c16a',
    400: '#f1a746', 500: '#ee8d21', 600: '#df7317', 700: '#b95815',
    800: '#934519', 900: '#773b17', 950: '#401c0a',
  },
} as const;

// ── Neeve named colors (matches tailwind.config.js `neeve.*` keys) ─────
export const NEEVE = {
  primary: CORE.neeveGreen,
  secondary: CORE.darkGreen,
  tertiary: CORE.neeveTertiary,
  accentBlue: BRAND.tertiary,
  yellow: '#FEBF58',
  orange: '#FF7E3F',
  mint: '#E8FCF5',
  lightBlue: '#B8E8F2',
} as const;
